// @flow
import React, { Component } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import Button from "elevate-ui/Button";
import Typography from "elevate-ui/Typography";
import Input from "elevate-ui/Input";
import Textarea from "elevate-ui/Textarea";
import Select from "elevate-ui/Select";
import Captcha from "../common/Captcha";
import { withGlobalContext } from "../common/GlobalContext";
import PhoneInput from "../common/PhoneInput";
import FormSectionHeading from "../common/FormSectionHeading";

type $Props = {
  classes: Object,
  className: string,
  previousFormFieldValues: Object,
  siteUser: Object,
  states: Array<string>,
  cta: string,
  assertCaptcha: Function,
  assertCaptchaV2: Function,
  isPageForceV2Captcha: Function,
  onReadCampaign: Function,
  isCanadian: Function,
};

type $State = {
  token: string,
};

class SellForm extends Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);
    this.state = {
      token: "",
    };
  }

  componentDidMount = () => {
    const { assertCaptchaV2, isPageForceV2Captcha } = this.props;
    if (isPageForceV2Captcha()) {
      assertCaptchaV2();
    }
  };

  onSubmit = () => {
    const { assertCaptcha, isPageForceV2Captcha } = this.props;
    if (isPageForceV2Captcha()) {
      // $FlowIgnore
      document.getElementById("sell-form").submit();
    } else {
      assertCaptcha("sell").then((token) => {
        this.setState({ token }, () => {
          // $FlowIgnore
          document.getElementById("sell-form").submit();
        });
      });
    }
  };

  render() {
    const {
      classes,
      className,
      previousFormFieldValues,
      siteUser,
      states,
      cta,
      onReadCampaign,
      isPageForceV2Captcha,
      isCanadian,
    } = this.props;

    const { utm_campaign } = onReadCampaign() || {};

    return (
      <div className={classNames(classes.root, className)}>
        <Typography type="heading3" className={classes.heading}>
          {cta}
        </Typography>
        <Typography type="body" className={classes.subHeading}>
          We're ready to help you sell your property. Contact us by filling out
          the form below and we'll get in touch with you shortly.
        </Typography>
        <Formik
          initialValues={{
            firstName:
              (previousFormFieldValues && previousFormFieldValues.firstName) ||
              (siteUser && siteUser.firstName) ||
              "",
            lastName:
              (previousFormFieldValues && previousFormFieldValues.lastName) ||
              (siteUser && siteUser.lastName) ||
              "",
            email:
              (previousFormFieldValues && previousFormFieldValues.email) ||
              (siteUser && siteUser.email) ||
              "",
            phone:
              (previousFormFieldValues && previousFormFieldValues.phone) ||
              (siteUser && siteUser.phone) ||
              "",
            streetAddress:
              (previousFormFieldValues &&
                previousFormFieldValues.streetAddress) ||
              "",
            streetAddress2:
              (previousFormFieldValues &&
                previousFormFieldValues.streetAddress2) ||
              "",
            city:
              (previousFormFieldValues && previousFormFieldValues.city) || "",
            state:
              (previousFormFieldValues && previousFormFieldValues.state) || "",
            zipCode:
              (previousFormFieldValues && previousFormFieldValues.zipCode) ||
              "",
            estimatedPrice:
              (previousFormFieldValues &&
                previousFormFieldValues.estimatedPrice) ||
              "",
            yearBuilt:
              (previousFormFieldValues && previousFormFieldValues.yearBuilt) ||
              "",
            bedrooms:
              (previousFormFieldValues && previousFormFieldValues.bedrooms) ||
              "1",
            bathrooms:
              (previousFormFieldValues && previousFormFieldValues.bathrooms) ||
              "1",
            description:
              (previousFormFieldValues &&
                previousFormFieldValues.description) ||
              "",
            utm_campaign: utm_campaign || "",
          }}
          validationSchema={() =>
            Yup.object().shape({
              firstName: Yup.string().required("First name is required"),
              lastName: Yup.string().required("Last name is required"),
              email: Yup.string()
                .email("Invalid email address")
                .required("Email is required"),
              phone: Yup.string().matches(
                /^\([0-9]{3}\) [0-9]{3}-[0-9]{4}$/,
                "Please enter valid phone number"
              ),
              streetAddress: Yup.string(),
              streetAddress2: Yup.string(),
              city: Yup.string(),
              state: Yup.string(),
              zipCode: Yup.string().required("Zip code is required"),
              estimatedPrice: Yup.string().required(
                "Estimated price is required"
              ),
              yearBuilt: Yup.string(),
              bedrooms: Yup.string(),
              bathrooms: Yup.string(),
              description: Yup.string(),
            })
          }
          onSubmit={this.onSubmit}
          render={({ isSubmitting }) => (
            <Form method="POST" id="sell-form" noValidate>
              <FormSectionHeading title="Contact Information" />
              <div className={classes.row}>
                <Field
                  id="firstName"
                  name="firstName"
                  label="First Name"
                  component={Input}
                />
                <Field
                  id="lastName"
                  name="lastName"
                  label="Last Name"
                  component={Input}
                />
              </div>
              <div className={classes.row}>
                <Field
                  id="email"
                  name="email"
                  label="Email"
                  component={Input}
                />
                <Field
                  id="phone"
                  name="phone"
                  label="Phone"
                  component={PhoneInput}
                />
              </div>
              <FormSectionHeading title="Property Address" />
              <div className={classes.row}>
                <Field
                  id="streetAddress"
                  name="streetAddress"
                  label="Address"
                  component={Input}
                />
              </div>
              <div className={classes.row}>
                <Field
                  id="streetAddress2"
                  name="streetAddress2"
                  label="Address 2"
                  component={Input}
                />
              </div>
              <div className={classes.row}>
                <Field id="city" name="city" label="City" component={Input} />
                <div className={classes.stateDropdown}>
                  <Field
                    id="state"
                    name="state"
                    label="State"
                    component={Select}
                    items={states}
                  />
                </div>
                <Field
                  id="zipCode"
                  name="zipCode"
                  label={isCanadian ? "Postal Code" : "Zip Code"}
                  component={Input}
                />
              </div>
              <FormSectionHeading title="Property Details" />
              <div className={classes.row}>
                <Field
                  id="estimatedPrice"
                  name="estimatedPrice"
                  label="Estimated Price"
                  component={Input}
                />
                <Field
                  id="yearBuilt"
                  name="yearBuilt"
                  label="Year Built"
                  component={Input}
                />
                <Field
                  id="bedrooms"
                  name="bedrooms"
                  label="Bedrooms"
                  component={Input}
                />
                <Field
                  id="bathrooms"
                  name="bathrooms"
                  label="Bathrooms"
                  component={Input}
                />
              </div>
              <div className={classes.row}>
                <Field
                  id="description"
                  name="description"
                  label="Additional Details"
                  component={Textarea}
                />
              </div>
              <Field
                id="utm_campaign"
                name="utm_campaign"
                component={Input}
                type="hidden"
              />
              <Captcha
                token={this.state.token}
                isPageForceV2Captcha={isPageForceV2Captcha()}
              />
              <Button
                type="submit"
                className={classNames(classes.sellBtn)}
                disabled={isSubmitting}
              >
                <span className={classes.sellText}>Contact Agent</span>
              </Button>
            </Form>
          )}
        />
      </div>
    );
  }
}

export default withStyles((theme) => ({
  "@global": {
    body: {
      backgroundColor: "#fff !important",
    },
  },
  root: {
    display: "flex",
    flexDirection: "column",
    maxWidth: "500px",
    padding: "0px 12px",
    margin: "96px auto",
  },
  heading: {
    textAlign: "center",
    marginBottom: theme.spacing.unit * 0.75,
  },
  subHeading: {
    textAlign: "center",
    margin: "auto",
    marginBottom: theme.spacing.unit * 0.75,
    maxWidth: "400px",
  },
  stateDropdown: {
    width: "100%",

    [theme.breakpoints(600)]: {
      minWidth: "160px",
    },
  },
  row: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",

    [theme.breakpoints(600)]: {
      flexWrap: "nowrap",

      "& > * + *": {
        marginLeft: theme.spacing.unit * 0.75,
      },
    },
  },
  sellBtn: {
    width: "100%",
    height: "54px",
    margin: "16px 0",
    fontSize: "24px",
    fontWeight: "600",
  },
  sellText: {
    fontSize: "20px",
  },
}))(withGlobalContext(SellForm));
